import Locale from '@enos/dpl/lib/locale';
import dplen from '@enos/dpl/lib/locale/lang/en_US';
import dplzh from '@enos/dpl/lib/locale/lang/zh_CN';
import dpljp from '@enos/dpl/lib/locale/lang/ja_JP';
import dples from '@enos/dpl/lib/locale/lang/es_ES';
import localeText, {getLocale, setLocale, setLocaleToCookie} from '@locale';
import {
  LAN_ENGLISH,
  LAN_CHINESE,
  LAN_SPANISH,
  LAN_JAPANESE,
  LAN_CHINESE_KEY,
  LAN_ENGLISH_KEY,
  LAN_JAPANESE_KEY,
  LAN_SPANISH_KEY,
  USER_INFO_STORAGE_KEY,
  USER_TYPE_SESSION_KEY,
  ACCESS_TOKEN_SESSION_KEY
} from '~/utils/constants';
import Error from '~/components/Modal/Error';
import SessionStore from '~/stores/SessionStore';
import _ from 'lodash';
import {iconArray} from '~/assets/icons/app-portal-icon/index';

export function fisrtToLowercase(str) {
  return str.charAt(0).toLowerCase() + str.substr(1);
}

export function switchLocale(key) {
  if (key !== getLocale()) {
    setLocale(key);
    setLocaleToCookie(key, document.location.protocol.split(':')[0] === 'https' && {
      Secure: true,
      SameSite: 'None'
    });
    localStorage.setItem('locale', key);
    window.location.reload();
  }
}

export function enableDplLang() {
  const currentLan = getLocale().toLowerCase();
  if (Locale && Locale.use) {
    if (currentLan.includes('en')) {
      Locale.use(dplen);
    } else if (currentLan.includes('zh')) {
      Locale.use(dplzh);
    } else if (currentLan.includes('ja')) {
      Locale.use(dpljp);
    } else {
      Locale.use(dples);
    }
  }
}

export const debounce = (func, wait = 1000, immediate = false) => {
  let timeout;

  return (...args) => {
    const context = this;

    const later = () => {
      timeout = null;
      if (!immediate) {
        func.apply(context, args);
      }
    };

    const callNow = immediate && !timeout;
    console.log(timeout);
    clearTimeout(timeout);

    timeout = setTimeout(later, wait);

    if (callNow) {
      func.apply(context, args);
    }
  };
};

export function showErrorModal(message, callback) {
  Error({
    title: localeText('COMMON.TITLE_ERROR'),
    content: message || localeText('COMMON.MSG_SERVER_ERROR'),
    onOk() {
      if (typeof callback === 'function') {
        callback();
      }
    }
  });
}

export function emailFormatValidate(email) {
  return /^([A-Za-z0-9_\-.])+@([A-Za-z0-9_\-.])+\.([A-Za-z]{2,4})$/.test(email);
}

export function querystring(name, url = window.location.href) {
  const n = name.replace(/[[]]/g, '\\$&');

  const regex = new RegExp(`[?&]${n}(=([^&#]*)|&|#|$)`, 'i');
  const results = regex.exec(url);

  if (!results) {
    return null;
  }
  if (!results[2]) {
    return '';
  }

  return decodeURIComponent(results[2].replace(/\+/g, ' '));
}

export function getRedirectUrl() {
  return window.location.href.split(window.location.origin)[1];
}

export function getBase64(img, callback) {
  const reader = new FileReader();
  reader.addEventListener('load', () => callback(reader.result));
  reader.readAsDataURL(img);
}

export function getLocationInfo(location) {
  const params = location.pathname.split('/');
  const infos = {
    search: window.location.search,
    hash: window.location.hash
  };
  params.forEach((v, i) => {
    switch (i) {
    case 2:
      infos.appId = v;
      break;
    case 3:
      infos.menuCode = v;
      break;
    default:
      break;
    }
  });
  return infos;
}

export function sessionExpired() {
  SessionStore.redirectToLogin(getRedirectUrl(), true);
}

export function redirectToSSO(url) {
  SessionStore.redirectToSSO(url);
}

export function clearLocaleStorage() {
  localStorage.removeItem(USER_INFO_STORAGE_KEY);
  localStorage.removeItem(USER_TYPE_SESSION_KEY);
  localStorage.removeItem(ACCESS_TOKEN_SESSION_KEY);
}

export function splitLocale(source) {
  const locale = getLocale().replace(/-/, '_');
  if (source) {
    return source[locale] || source.default;
  }
}

export function formatLanToText(code) {
  if (code === LAN_CHINESE_KEY) {
    return LAN_CHINESE;
  }
  if (code === LAN_ENGLISH_KEY) {
    return LAN_ENGLISH;
  }
  if (code === LAN_JAPANESE_KEY) {
    return LAN_JAPANESE;
  }
  return LAN_SPANISH;
}

export function formatLanToKey(input) {
  if (input === LAN_ENGLISH) {
    return LAN_ENGLISH_KEY;
  }
  if (input === LAN_CHINESE) {
    return LAN_CHINESE_KEY;
  }
  if (input === LAN_JAPANESE) {
    return LAN_JAPANESE_KEY;
  }
  return LAN_SPANISH_KEY;
}

export function formatKeyToLocale(key) {
  if (key === LAN_SPANISH_KEY) {
    return {key: LAN_SPANISH_KEY, label: localeText('COMMON.TXT_LANGUAGE_ES')};
  }
  if (key === LAN_CHINESE_KEY) {
    return {key: LAN_CHINESE_KEY, label: localeText('COMMON.TXT_LANGUAGE_ZH')};
  }
  if (key === LAN_JAPANESE_KEY) {
    return {key: LAN_JAPANESE_KEY, label: localeText('COMMON.TXT_LANGUAGE_JA')};
  }

  return {key: LAN_ENGLISH_KEY, label: localeText('COMMON.TXT_LANGUAGE_EN')};
}

export function isMobileDevice() {
  let isMobile = 0; // initiate as false
  // device detection
  // eslint-disable-next-line
  if (/(android|bb\d+|meego).+mobile|avantgo|bada\/|blackberry|blazer|compal|elaine|fennec|hiptop|iemobile|ip(hone|od)|ipad|iris|kindle|Android|Silk|lge |maemo|midp|mmp|netfront|opera m(ob|in)i|palm( os)?|phone|p(ixi|re)\/|plucker|pocket|psp|series(4|6)0|symbian|treo|up\.(browser|link)|vodafone|wap|windows (ce|phone)|xda|xiino/i.test(navigator.userAgent)
  // eslint-disable-next-line
    || /1207|6310|6590|3gso|4thp|50[1-6]i|770s|802s|a wa|abac|ac(er|oo|s\-)|ai(ko|rn)|al(av|ca|co)|amoi|an(ex|ny|yw)|aptu|ar(ch|go)|as(te|us)|attw|au(di|\-m|r |s )|avan|be(ck|ll|nq)|bi(lb|rd)|bl(ac|az)|br(e|v)w|bumb|bw\-(n|u)|c55\/|capi|ccwa|cdm\-|cell|chtm|cldc|cmd\-|co(mp|nd)|craw|da(it|ll|ng)|dbte|dc\-s|devi|dica|dmob|do(c|p)o|ds(12|\-d)|el(49|ai)|em(l2|ul)|er(ic|k0)|esl8|ez([4-7]0|os|wa|ze)|fetc|fly(\-|_)|g1 u|g560|gene|gf\-5|g\-mo|go(\.w|od)|gr(ad|un)|haie|hcit|hd\-(m|p|t)|hei\-|hi(pt|ta)|hp( i|ip)|hs\-c|ht(c(\-| |_|a|g|p|s|t)|tp)|hu(aw|tc)|i\-(20|go|ma)|i230|iac( |\-|\/)|ibro|idea|ig01|ikom|im1k|inno|ipaq|iris|ja(t|v)a|jbro|jemu|jigs|kddi|keji|kgt( |\/)|klon|kpt |kwc\-|kyo(c|k)|le(no|xi)|lg( g|\/(k|l|u)|50|54|\-[a-w])|libw|lynx|m1\-w|m3ga|m50\/|ma(te|ui|xo)|mc(01|21|ca)|m\-cr|me(rc|ri)|mi(o8|oa|ts)|mmef|mo(01|02|bi|de|do|t(\-| |o|v)|zz)|mt(50|p1|v )|mwbp|mywa|n10[0-2]|n20[2-3]|n30(0|2)|n50(0|2|5)|n7(0(0|1)|10)|ne((c|m)\-|on|tf|wf|wg|wt)|nok(6|i)|nzph|o2im|op(ti|wv)|oran|owg1|p800|pan(a|d|t)|pdxg|pg(13|\-([1-8]|c))|phil|pire|pl(ay|uc)|pn\-2|po(ck|rt|se)|prox|psio|pt\-g|qa\-a|qc(07|12|21|32|60|\-[2-7]|i\-)|qtek|r380|r600|raks|rim9|ro(ve|zo)|s55\/|sa(ge|ma|mm|ms|ny|va)|sc(01|h\-|oo|p\-)|sdk\/|se(c(\-|0|1)|47|mc|nd|ri)|sgh\-|shar|sie(\-|m)|sk\-0|sl(45|id)|sm(al|ar|b3|it|t5)|so(ft|ny)|sp(01|h\-|v\-|v )|sy(01|mb)|t2(18|50)|t6(00|10|18)|ta(gt|lk)|tcl\-|tdg\-|tel(i|m)|tim\-|t\-mo|to(pl|sh)|ts(70|m\-|m3|m5)|tx\-9|up(\.b|g1|si)|utst|v400|v750|veri|vi(rg|te)|vk(40|5[0-3]|\-v)|vm40|voda|vulc|vx(52|53|60|61|70|80|81|83|85|98)|w3c(\-| )|webc|whit|wi(g |nc|nw)|wmlb|wonu|x700|yas\-|your|zeto|zte\-/i.test(navigator.userAgent.substr(0, 4))
  ) {
    isMobile = 1;
  }

  return isMobile;
}

export function transformKeyToVar(keyObj) {
  const newObj = {};
  _.forEach(keyObj, (value, key) => {
    newObj[key.replace('-', '_')] = value;
  });
  return newObj;
}

export function transformVarToKey(varObj) {
  const newObj = {};
  if (typeof varObj === 'object' && _.size(varObj) > 0) {
    _.forEach(varObj, (value, key) => {
      newObj[key.replace('_', '-')] = value;
    });
  }

  return newObj;
}

export function checkIconExist(icon) {
  const iconClassName = `.${icon}`;
  if (iconArray?.indexOf(iconClassName) > -1) {
    return true;
  }
  return false;
}
